const SHARE_WIDTH = 720;
const SHARE_HEIGHT = 1080;

async function imageFrom(source: string) {
  const response = await fetch(source);
  if (!response.ok) throw new Error("Share image could not be loaded");
  const blob = await response.blob();
  if (typeof createImageBitmap === "function") return createImageBitmap(blob);
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob), image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Share image could not be decoded")); };
    image.src = url;
  });
}

function fitContain(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
) {
  const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);
  return {
    width: sourceWidth * scale,
    height: sourceHeight * scale,
  };
}

export async function createBrandedShareImage({
  source,
  title,
  kicker,
}: {
  source?: string;
  title: string;
  kicker: string;
}) {
  const canvas = document.createElement("canvas");
  canvas.width = SHARE_WIDTH;
  canvas.height = SHARE_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image creation is unavailable in this browser");

  const background = context.createLinearGradient(0, 0, SHARE_WIDTH, SHARE_HEIGHT);
  background.addColorStop(0, "#071216");
  background.addColorStop(1, "#020607");
  context.fillStyle = background;
  context.fillRect(0, 0, SHARE_WIDTH, SHARE_HEIGHT);

  context.fillStyle = "rgba(81, 196, 215, 0.08)";
  for (let y = 0; y < SHARE_HEIGHT; y += 24) {
    for (let x = (y / 24) % 2 ? 24 : 0; x < SHARE_WIDTH; x += 48)
      context.fillRect(x, y, 24, 24);
  }

  if (source) {
    try {
      const image = await imageFrom(source);
      const fitted = fitContain(image.width, image.height, 656, 880);
      const x = (SHARE_WIDTH - fitted.width) / 2;
      const y = 112 + (880 - fitted.height) / 2;
      context.fillStyle = "#020506";
      context.fillRect(30, 96, 660, 884);
      context.imageSmoothingEnabled = false;
      context.drawImage(image, x, y, fitted.width, fitted.height);
      if ("close" in image) image.close();
    } catch (error) {
      console.warn("MatterMaze share capture fallback used.", error);
    }
  }

  context.fillStyle = "rgba(2, 7, 9, 0.94)";
  context.fillRect(0, 0, SHARE_WIDTH, 112);
  context.fillRect(0, 970, SHARE_WIDTH, 110);
  context.strokeStyle = "#4f747b";
  context.lineWidth = 3;
  context.strokeRect(18, 18, SHARE_WIDTH - 36, SHARE_HEIGHT - 36);

  try {
    const logo = await imageFrom("/mattermaze.png");
    const fitted = fitContain(logo.width, logo.height, 244, 76);
    context.drawImage(logo, 34, 18 + (76 - fitted.height) / 2, fitted.width, fitted.height);
    if ("close" in logo) logo.close();
  } catch (error) {
    console.warn("MatterMaze logo could not be added to share image.", error);
  }

  context.textAlign = "right";
  context.fillStyle = "#91d867";
  context.font = "700 19px monospace";
  context.fillText(kicker.toUpperCase(), SHARE_WIDTH - 34, 55);
  context.fillStyle = "#dce8e4";
  context.font = "700 26px monospace";
  context.fillText(title.toUpperCase().slice(0, 38), SHARE_WIDTH - 34, 1024);
  context.fillStyle = "#80a0a5";
  context.font = "700 15px monospace";
  context.fillText("PLAY · BUILD · ESCAPE", SHARE_WIDTH - 34, 1052);

  return canvas.toDataURL("image/png");
}

export async function dataUrlFile(dataUrl: string, filename: string) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], filename, { type: "image/png" });
}
